import { iterateEntries, IterUtils } from "../../../../Common/IterableUtils";
import { createAbsolute, createModelPath } from "../../../../Common/ModelPath/ModelPath";
import {
    ICandyApiDraftAttachmentsClient,
    ICandyApiDraftClient,
    PathLoadSpecification,
} from "../../../../Common/CandyApiClient/ICandyApiClient";
import { fixInnerTypesFromCandyBackend } from "../../Engine/BackendInnerHelpers";
import { ModelHunk } from "../../Engine/Core/ModelStoring/ModelHunk";
import { AttachmentState } from "../../Engine/Externals/Attachments/Store/AttachmentsReducer";
import { IForm, RequisitesInfo } from "../../Engine/Form/IForm";
import { getDraftFullId } from "../Types/FormSettings";
import { getFakeRequisitesForDraft } from "../../Engine/Fakes/FakeRequisites";
import { FeatureFlags } from "../Types/FeatureFlags";
import { ServerRawResponse } from "../../../../Common/CandyApiClient/Types/GetInner/ServerRawResponse";
import { ServerJsonResponse } from "../../../../Common/CandyApiClient/Types/GetInner/ServerInnerResponse";
import { DraftFullId } from "../../../../Common/CandyApiClient/Types/DraftFullId";

export async function prepareLazyLoadingManager(form: IForm, skipNormalization: boolean = false): Promise<void> {
    if (form.lazyLoadingManager.hasLazyLoadRules()) {
        const checkPath = createAbsolute(`/${form.settings.formRoot}/LazyLoadingEnabled.value`);
        const paths: PathLoadSpecification[] = [{ pathOrPathPrefix: checkPath, skip: false }];
        const data = await loadInner(
            form.candyApiDraftClient,
            form.settings.useServerSideFLangNormalization,
            paths,
            skipNormalization
        );
        fixInnerTypesFromCandyBackend(data);
        const resultValue = data.get(createModelPath(`/${form.settings.formRoot}/LazyLoadingEnabled`, true))?.value;
        const isEnabled = resultValue === "true";
        form.lazyLoadingManager.setLazyLoadingEnabledMode(isEnabled);
    }
}

export async function fetchInner(
    form: IForm,
    initialModel: undefined | { [path: string]: unknown },
    skipNormalization?: boolean
): Promise<ModelHunk> {
    if (initialModel) {
        const inner = initialModelToInner(initialModel);
        fixInnerTypesFromCandyBackend(inner);
        return inner;
    }

    const paths = IterUtils.concat<PathLoadSpecification>(
        [{ pathOrPathPrefix: "*", skip: false }],
        Iterator.from(form.lazyLoadingManager.getServerOnlyRules()).map(x => ({
            pathOrPathPrefix: x.modelPath,
            skip: x.skip,
        })),
        form.lazyLoadingManager.isLazyLoadingEnabled()
            ? Iterator.from(form.lazyLoadingManager.getInitialLazyLoadRules()).map(x => ({
                  pathOrPathPrefix: x.modelPath,
                  skip: x.skip,
              }))
            : []
    ).toArray();

    const inner = await loadInner(
        form.candyApiDraftClient,
        form.settings.useServerSideFLangNormalization,
        paths,
        skipNormalization
    );
    fixInnerTypesFromCandyBackend(inner);
    return inner;
}

export async function fetchDataFromServer(
    form: IForm,
    requisiteList: string[]
): Promise<[RequisitesInfo, AttachmentState, FeatureFlags]> {
    await form.candyApiDraftClient.setDraftSettings({ isReadOnly: form.settings.isReadOnly });

    const attachmentsPromise = loadAttachments(form.candyApiDraftClient);
    const requisitesPromise = fetchRequisites(form.candyApiDraftClient, requisiteList, getDraftFullId(form.settings));
    const featureFlagsPromise = form.candyApi.getFeatureFlags(
        form.settings.abonId,
        form.settings.orgId,
        form.settings.userId
    );

    const [requisites, attachments, featureFlags] = await Promise.all([
        requisitesPromise,
        attachmentsPromise,
        featureFlagsPromise,
    ]);

    return [requisites, attachments, featureFlags];
}

export async function loadAttachments(
    draftAttachmentsClient: ICandyApiDraftAttachmentsClient
): Promise<AttachmentState> {
    const result: AttachmentState = {
        byId: {},
        allIds: [],
    };

    const attachments = await draftAttachmentsClient.getAllAttachments();
    for (const attachment of attachments) {
        result.byId[attachment.id] = attachment;
        result.allIds.push(attachment.id);
    }

    return result;
}

export function prepareJsonDataFromServer(innerServerResponse: ServerJsonResponse) {
    const modelHunk = new ModelHunk();
    modelHunk.fillFromServerJsonResponse(innerServerResponse);
    fixInnerTypesFromCandyBackend(modelHunk);
    return modelHunk;
}

export function prepareRawDataFromServer(innerServerResponse: ServerRawResponse) {
    const modelHunk = new ModelHunk();
    modelHunk.fillFromServerRawResponse(innerServerResponse);
    fixInnerTypesFromCandyBackend(modelHunk);
    return modelHunk;
}

export async function loadInner(
    candyApiDraftClient: ICandyApiDraftClient,
    useServerSideFLangNormalization: boolean | undefined,
    loadingPathSpecs: readonly PathLoadSpecification[] = [],
    skipNormalization: boolean = false
): Promise<ModelHunk> {
    const modelHunk = useServerSideFLangNormalization
        ? prepareRawDataFromServer(
              await candyApiDraftClient.getRawPartialInnerAsJsonTree(loadingPathSpecs, undefined, skipNormalization)
          )
        : prepareJsonDataFromServer(
              await candyApiDraftClient.getPartialInnerAsJsonTree(loadingPathSpecs, undefined, skipNormalization)
          );
    return modelHunk;
}

export function initialModelToInner(initialModel: { [path: string]: unknown }): ModelHunk {
    const result = new ModelHunk();
    for (const [path, value] of iterateEntries(initialModel)) {
        if (!path.includes(".") && typeof value === "string") {
            result.set(createModelPath(path, true), { value: value });
        }
    }
    return result;
}

export async function fetchRequisites(
    candyApiDraftClient: ICandyApiDraftClient,
    requisiteList: string[],
    draftFullId: DraftFullId
): Promise<RequisitesInfo> {
    return (
        getFakeRequisitesForDraft(draftFullId, requisiteList) ??
        (requisiteList.length > 0 ? candyApiDraftClient.getRequisites(requisiteList) : {})
    );
}
